import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useGameContext } from '../../contexts/GameContext';
import { useUserContext } from '../../contexts/UserContext';
import { MAX_EXP } from '../../constants';
import { useSettings } from '../../contexts/SettingsContext';
import { Chord } from '../../types/chord';
import logger from '../../utils/logger';

let soundUtils: typeof import('../../utils/soundUtils') | null = null;

// Level Selection Modal Component
interface LevelSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLevel: number;
  completedLevels: Set<number>;
  onSelectLevel: (level: number) => void;
  isDarkMode: boolean;
}

const LevelSelectionModal: React.FC<LevelSelectionModalProps> = ({
  isOpen,
  onClose,
  currentLevel,
  completedLevels,
  onSelectLevel,
  isDarkMode,
}) => {
  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="absolute inset-0 z-10" onClick={onClose} />

      <div
        className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}
p-6 rounded-lg shadow-xl w-full max-w-xl max-h-[85vh] overflow-y-auto
transform transition-all duration-200 scale-100 z-20 relative`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Select Level</h2>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Level Grid */}
        <div className="grid grid-cols-5 sm:grid-cols-5 md:grid-cols-10 gap-2">
          {Array.from({ length: 100 }, (_, i) => i + 1).map((num) => {
            const isCompleted = completedLevels.has(num);
            return (
              <button
                key={num}
                className={`py-1 px-1 rounded-md text-sm transition-transform duration-100 ease-out hover:-translate-y-0.5 active:scale-95
${num === currentLevel
? 'bg-blue-500 text-white'
: isCompleted
? isDarkMode
? 'bg-green-700 hover:bg-green-600 text-white'
: 'bg-green-500 hover:bg-green-400 text-white'
: isDarkMode
? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
: 'bg-gray-200 hover:bg-gray-300 text-gray-800'
}`}
                onClick={() => onSelectLevel(num)}
              >
                {num}
                {isCompleted &&
                  <span className="ml-1 text-xs">✓</span>
                }
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Main StatusBar Component
const StatusBar: React.FC = () => {
  const { level, setLevel, exp } = useGameContext();
  const { completedLevels } = useUserContext();
  const { isDarkMode, chordNotationType } = useSettings();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newChordsList, setNewChordsList] = useState<Chord[]>([]);
  const [isNewChordsLoading, setIsNewChordsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const levelCacheRef = useRef<Record<number, Chord[]>>({});

  // Calculate progress
  const isLevelComplete = completedLevels.has(level);
  const progressPercentage = Math.min((exp / MAX_EXP) * 100, 100);

  const handleLevelSelection = useCallback((selectedLevel: number) => {
    setLevel(selectedLevel);
    setIsModalOpen(false);
  }, [setLevel]);

  // Fetch level info when `level` changes
  useEffect(() => {
    const fetchLevelInfo = async () => {
      // Return cached data if available
      if (levelCacheRef.current[level]) {
        setNewChordsList(levelCacheRef.current[level]);
        return;
      }

      if (isLoadingRef.current) return;
      setIsNewChordsLoading(true);

      try {
        isLoadingRef.current = true;
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/v1/levels/${level}`);
        if (!response.ok) throw new Error('Failed to fetch level info');

        const levelData = await response.json();
        const list = (levelData.new_chords || '')
          .split(' ')
          .filter(Boolean)
          .map(Chord.fromString);

        // Store in cache and update state
        levelCacheRef.current[level] = list;
        setNewChordsList(list);
      } catch (error) {
        logger.error({ err: error }, 'Error fetching level info');
        setNewChordsList([]);
      } finally {
        isLoadingRef.current = false;
        setIsNewChordsLoading(false);
      }
    };

    fetchLevelInfo();
  }, [level]);

  const handleChordClick = async (chord: Chord) => {
    if (!soundUtils) {
      soundUtils = await import('../../utils/soundUtils');
    }
    if (soundUtils) {
      soundUtils.playChord(chord, 0.5); // Play sound
    }
  };

  return (
    <>
      <div className={`mb-6 p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg`}>
        {/* Header */}
        <div className="flex justify-between items-center mb-2">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full ${
              isLevelComplete ? 'bg-green-500' : 'bg-blue-500'
            } text-white text-lg font-bold cursor-pointer transition-shadow hover:shadow-md`}
            onClick={() => setIsModalOpen(true)}
          >
            Level {level} {isLevelComplete && '✓'}
          </span>
          <div className="text-sm flex items-center" title="EXP gained by correct answers">
            EXP: {exp.toFixed(1)} / {MAX_EXP}
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative">
          <div className={`w-full h-4 rounded-full overflow-hidden mb-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className={`h-full ${isLevelComplete ? 'bg-green-500' : 'bg-blue-500'} transition-all duration-500 ease-out`}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* New chords section */}
        <div className="mt-2 text-sm">
          <span className="font-medium">New chords:</span>{' '}
          {isNewChordsLoading ? (
            <div className="inline-flex space-x-2 animate-pulse">
              {[...Array(3)].map((_, idx) => (
                <span key={idx} className="h-4 w-12 bg-gray-300 dark:bg-gray-600 rounded-full" />
              ))}
            </div>
          ) : (
            newChordsList.map((chord, i) => (
              <button
                key={i}
                type="button"
                className="inline-block mr-1 mb-1 px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-xs cursor-help transition-transform duration-100 ease-out hover:-translate-y-0.5 font-normal border-0 appearance-none"
                title={chord.toString()}
                dangerouslySetInnerHTML={{ __html: chord.toString(chordNotationType) }}
                onClick={() => handleChordClick(chord)}
                onTouchStart={() => handleChordClick(chord)}
              />
            ))
          )}
        </div>
      </div>

      {/* Level Selection Modal */}
      <LevelSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        completedLevels={completedLevels}
        currentLevel={level}
        onSelectLevel={handleLevelSelection}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default StatusBar;

